import React, { useEffect, useState } from "react";
import { ShieldAlert, Loader2, RefreshCw, Ban, Check, Undo2, Eye, AlertTriangle, UserX } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import ProfileBlock from "../../components/admin/ProfileBlock";
import { PageHeader, EmptyState, LoadingBlock, StatCard } from "../../components/app/ui";
import { reportsApi, errorMessage, formatDate, titleCase, inr } from "../../lib/api";

const TABS = ["open", "resolved"];

function ContextCard({ ctx }) {
  if (!ctx) return <p className="text-sm text-ink-muted">The related {`trip/order`} could not be found (it may have been deleted).</p>;
  return (
    <div className="rounded-2xl border border-surface-200 bg-surface-50 p-4" data-testid="report-context">
      <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-ink-muted">What happened · {titleCase(ctx.type.replace("_", " "))}</p>
      <div className="grid gap-1 text-sm text-ink-soft">
        {ctx.status && <p><span className="text-ink-muted">Status:</span> <span className="font-medium text-ink">{titleCase(ctx.status.replace(/_/g, " "))}</span></p>}
        {ctx.type === "ride" && <>
          <p><span className="text-ink-muted">Pickup:</span> {ctx.pickup_address}</p>
          <p><span className="text-ink-muted">Drop:</span> {ctx.drop_address}</p>
          <p><span className="text-ink-muted">Fare:</span> {ctx.fare != null ? inr(ctx.fare) : "—"} · {ctx.distance_km} km · {ctx.vehicle_type}</p>
        </>}
        {ctx.type === "order" && <>
          <p><span className="text-ink-muted">Restaurant:</span> {ctx.restaurant_name}</p>
          <p><span className="text-ink-muted">Items:</span> {(ctx.items || []).map((it) => `${it.qty || 1}× ${it.name}`).join(", ") || "—"}</p>
          <p><span className="text-ink-muted">Total:</span> {ctx.total != null ? inr(ctx.total) : "—"}</p>
          <p><span className="text-ink-muted">Deliver to:</span> {ctx.delivery_address}</p>
        </>}
        {ctx.type === "private_car" && <>
          <p><span className="text-ink-muted">Pickup:</span> {ctx.pickup_address}</p>
          {ctx.dest_address && <p><span className="text-ink-muted">Destination:</span> {ctx.dest_address}</p>}
          <p><span className="text-ink-muted">Car:</span> {ctx.car || "—"} · {ctx.days} day(s)</p>
        </>}
        {ctx.created_at && <p className="text-xs text-ink-muted">{formatDate(ctx.created_at)}</p>}
      </div>
    </div>
  );
}

export default function Reports() {
  const [status, setStatus] = useState("open");
  const [data, setData] = useState(null);
  const [counts, setCounts] = useState({});
  const [busy, setBusy] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = () => {
    setData(null);
    reportsApi.adminList(status).then((d) => { setData(d.reports || []); setCounts(d.counts || {}); }).catch((e) => { toast.error(errorMessage(e)); setData([]); });
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail({ loading: true });
    try { const d = await reportsApi.adminDetail(id); setDetail(d.report); }
    catch (e) { toast.error(errorMessage(e)); setDetail(null); }
    finally { setDetailLoading(false); }
  };

  const resolve = async (id, action, note) => {
    setBusy(id + action);
    try {
      await reportsApi.adminResolve(id, action, note || (action === "suspend" ? "Suspended after review" : action === "warn" ? "Warning issued after review" : action === "suspend_reporter" ? "Reporter suspended for abuse/false report" : "Dismissed"));
      toast.success(action === "suspend" ? "Partner suspended" : action === "warn" ? "Warning sent to partner" : action === "suspend_reporter" ? "Reporting customer suspended" : "Report dismissed");
      setDetail(null);
      load();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(null); }
  };
  const unsuspend = async (userId, id) => {
    setBusy(id + "unsuspend");
    try { await reportsApi.adminUnsuspend(userId); toast.success("Partner reinstated"); load(); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(null); }
  };

  return (
    <>
      <PageHeader title="Reports" description="Customer misconduct & harassment reports against partners." actions={<Button variant="outline" onClick={load} className="rounded-full" data-testid="reports-refresh"><RefreshCw className="mr-2 h-4 w-4" /> Refresh</Button>} />
      <div className="mb-6 grid gap-4 sm:grid-cols-2">
        {TABS.map((s) => <button key={s} onClick={() => setStatus(s)} className={`text-left ${status === s ? "ring-2 ring-brand rounded-2xl" : ""}`} data-testid={`reports-tab-${s}`}><StatCard label={titleCase(s)} value={counts[s] ?? "…"} icon={ShieldAlert} tone={s === "open" ? "brand" : "graphite"} /></button>)}
      </div>
      {data === null && <LoadingBlock rows={4} />}
      {data && data.length === 0 && <EmptyState icon={ShieldAlert} title={`No ${status} reports`} text="Nothing to review here." />}
      {data && data.length > 0 && (
        <div className="space-y-4" data-testid="reports-list">
          {data.map((r) => (
            <div key={r.report_id} className="kt-card p-5" data-testid={`report-${r.report_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 text-sm font-semibold text-ink">
                    <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">{(r.category || "").replace(/_/g, " ")}</span>
                    against {r.target_label || "partner"}
                    {r.target?.suspended && <span className="rounded-full bg-ink px-2 py-0.5 text-xs font-semibold text-white">Suspended</span>}
                    {r.upheld_reports >= 2 && <span className="rounded-full bg-red-600 px-2 py-0.5 text-xs font-semibold text-white" data-testid={`repeat-offender-${r.report_id}`}>⚠ Repeat offender · {r.upheld_reports}</span>}
                  </p>
                  <p className="mt-1 text-xs text-ink-soft">By {r.reporter_name} · {r.context_type} {String(r.context_id).slice(-6).toUpperCase()} · {formatDate(r.created_at)}</p>
                  {r.target && <p className="mt-1 text-xs text-ink-muted">Partner: {r.target.name} · {r.target.email}{r.target.phone ? ` · ${r.target.phone}` : ""}</p>}
                  {r.description && <p className="mt-2 rounded-xl bg-surface-100 p-3 text-sm text-ink-soft">{r.description}</p>}
                  <Button size="sm" variant="outline" className="mt-3 rounded-full" onClick={() => openDetail(r.report_id)} data-testid={`report-view-${r.report_id}`}><Eye className="mr-2 h-4 w-4" /> View details</Button>
                </div>
                {r.status === "open" ? (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => resolve(r.report_id, "suspend")} disabled={!!busy} className="rounded-full bg-red-600 text-white hover:bg-red-700" data-testid={`report-suspend-${r.report_id}`}>{busy === r.report_id + "suspend" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Ban className="mr-2 h-4 w-4" />} Suspend partner</Button>
                    <Button size="sm" variant="outline" onClick={() => resolve(r.report_id, "dismiss")} disabled={!!busy} className="rounded-full" data-testid={`report-dismiss-${r.report_id}`}><Check className="mr-2 h-4 w-4" /> Dismiss</Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-ink-soft">{titleCase((r.resolution || "").replace(/_/g, " ")) || "Resolved"}</span>
                    {r.target?.suspended && <Button size="sm" variant="outline" onClick={() => unsuspend(r.target_id, r.report_id)} disabled={!!busy} className="rounded-full" data-testid={`report-unsuspend-${r.report_id}`}><Undo2 className="mr-2 h-4 w-4" /> Reinstate</Button>}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="report-detail-dialog">
          <DialogHeader><DialogTitle>Misconduct report</DialogTitle><DialogDescription>Review the parties and incident, then verify and take action.</DialogDescription></DialogHeader>
          {detailLoading || detail?.loading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
          ) : detail ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-red-50 px-3 py-1 text-sm font-semibold text-red-700">{(detail.category || "").replace(/_/g, " ")}</span>
                <span className="text-xs text-ink-muted">{formatDate(detail.created_at)}</span>
              </div>
              {detail.description && <div className="rounded-2xl bg-surface-100 p-4 text-sm text-ink-soft" data-testid="report-detail-comment"><p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-ink-muted">Reporter's comment</p>{detail.description}</div>}
              {detail.photo_ids?.length > 0 && (
                <div data-testid="report-detail-evidence">
                  <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-ink-muted">Evidence ({detail.photo_ids.length})</p>
                  <div className="flex flex-wrap gap-2">
                    {detail.photo_ids.map((pid) => (
                      <a key={pid} href={reportsApi.photoUrl(pid)} target="_blank" rel="noreferrer" data-testid={`report-evidence-${pid}`}>
                        <img src={reportsApi.photoUrl(pid)} alt="evidence" className="h-24 w-24 rounded-xl border border-surface-200 object-cover" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
              <ContextCard ctx={detail.context} />
              <ProfileBlock profile={detail.reporter} heading="Reporter (customer)" />
              <ProfileBlock profile={detail.target} heading="Reported partner" />
              {detail.delivery_driver && <ProfileBlock profile={detail.delivery_driver} heading="Delivery partner on this order" />}
              {detail.status === "open" ? (
                <div className="flex flex-wrap gap-2 border-t border-surface-200 pt-4">
                  <Button size="sm" onClick={() => resolve(detail.report_id, "suspend")} disabled={!!busy} className="rounded-full bg-red-600 text-white hover:bg-red-700" data-testid="detail-suspend-partner"><Ban className="mr-2 h-4 w-4" /> Suspend partner</Button>
                  <Button size="sm" onClick={() => resolve(detail.report_id, "warn")} disabled={!!busy} className="rounded-full bg-amber-500 text-white hover:bg-amber-600" data-testid="detail-warn-partner"><AlertTriangle className="mr-2 h-4 w-4" /> Warn partner</Button>
                  <Button size="sm" variant="outline" onClick={() => resolve(detail.report_id, "suspend_reporter")} disabled={!!busy} className="rounded-full text-red-600" data-testid="detail-suspend-reporter"><UserX className="mr-2 h-4 w-4" /> Suspend reporter</Button>
                  <Button size="sm" variant="outline" onClick={() => resolve(detail.report_id, "dismiss")} disabled={!!busy} className="rounded-full" data-testid="detail-dismiss"><Check className="mr-2 h-4 w-4" /> Dismiss</Button>
                </div>
              ) : (
                <div className="border-t border-surface-200 pt-4 text-sm text-ink-soft" data-testid="report-detail-resolution">Resolution: <span className="font-semibold text-ink">{titleCase((detail.resolution || "").replace(/_/g, " "))}</span>{detail.resolution_note ? ` — ${detail.resolution_note}` : ""}</div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
